import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Analytics Page Component
 *
 * Track performance metrics with inline styles for WordPress admin compatibility.
 * Displays real data from enhanced API endpoints.
 *
 * @package WooAIChatbot
 */
import { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { TrendingUp, MessageSquare, Users, Clock, Download, RefreshCw, ArrowUp, ArrowDown, BarChart3, Calendar, ShoppingCart } from 'lucide-react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTranslation } from '../../hooks/useTranslation';
import { HourlyUsageChart } from '../../components/analytics/HourlyUsageChart';
// Inline styles
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    dateRangeSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        padding: '4px',
    },
    dateRangeButton: {
        height: '32px',
        padding: '0 12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#374151',
        fontSize: '14px',
        cursor: 'pointer',
    },
    dateRangeButtonActive: {
        height: '32px',
        padding: '0 12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        cursor: 'pointer',
    },
    exportButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 16px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    exportButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 16px',
        borderRadius: '10px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        cursor: 'pointer',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '24px',
    },
    statCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '36px',
    },
    iconBox: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    statContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    statLabel: {
        fontSize: '14px',
        color: '#6b7280',
    },
    statValue: {
        fontSize: '30px',
        fontWeight: 700,
        lineHeight: '36px',
        color: '#111827',
    },
    trendRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    trendUp: {
        color: '#10b981',
    },
    trendDown: {
        color: '#ef4444',
    },
    trendText: {
        fontSize: '14px',
        color: '#6b7280',
    },
    chartsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '24px',
    },
    chartCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '48px',
    },
    chartHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    chartTitle: {
        fontSize: '18px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    viewDetailsLink: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#6366f1',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    },
    chartPlaceholder: {
        flex: 1,
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '250px',
    },
    placeholderContent: {
        textAlign: 'center',
        color: '#6b7280',
    },
    chartFooter: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb',
    },
    footerStat: {
        textAlign: 'center',
    },
    footerValue: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#111827',
    },
    footerLabel: {
        fontSize: '12px',
        color: '#6b7280',
    },
    queriesCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    queryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: '1px solid #f3f4f6',
    },
    queryRowLast: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 0',
        borderBottom: 'none',
    },
    queryLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
    },
    queryRank: {
        fontSize: '18px',
        fontWeight: 600,
        width: '24px',
        color: '#9ca3af',
    },
    queryInfo: {
        flex: 1,
    },
    queryText: {
        fontWeight: 500,
        color: '#111827',
    },
    queryAsks: {
        fontSize: '14px',
        color: '#6b7280',
    },
    queryTrend: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    funnelCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    funnelStage: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    funnelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    funnelLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#111827',
    },
    funnelValue: {
        fontSize: '14px',
        color: '#6b7280',
    },
    funnelBar: {
        height: '12px',
        borderRadius: '9999px',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
    },
    funnelProgress: {
        height: '100%',
        borderRadius: '9999px',
        transition: 'width 0.3s',
    },
    usageCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '48px',
    },
    usageHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    usageTitle: {
        fontWeight: 400,
        color: '#111827',
        margin: 0,
    },
    usageChart: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '8px',
        height: '200px',
    },
    usageBar: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        justifyContent: 'flex-end',
    },
    usageBarFill: {
        width: '100%',
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#6366f1',
        minHeight: '4px',
    },
    usageLabel: {
        fontSize: '12px',
        color: '#6b7280',
    },
    loading: {
        padding: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
    },
    loadingContent: {
        textAlign: 'center',
    },
    spinner: {
        width: '32px',
        height: '32px',
        margin: '0 auto 8px',
        color: '#6366f1',
    },
    errorCard: {
        padding: '24px',
        borderRadius: '14px',
        border: '1px solid #ef4444',
        backgroundColor: '#fef2f2',
    },
    errorTitle: {
        fontWeight: 600,
        color: '#ef4444',
        marginBottom: '8px',
    },
    errorMessage: {
        fontSize: '14px',
        color: '#374151',
        marginBottom: '16px',
    },
    retryButton: {
        padding: '8px 16px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    noDataCard: {
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: '32px',
        textAlign: 'center',
        color: '#6b7280',
    },
    dateRangeInfo: {
        fontSize: '12px',
        color: '#9ca3af',
        marginTop: '4px',
    },
};
// Helper functions
const formatNumber = (num) => {
    if (num === undefined || num === null)
        return '0';
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
};
const formatDuration = (seconds) => {
    if (!seconds)
        return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
        return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
};
const formatPercentage = (value) => {
    if (value === undefined || value === null)
        return '0%';
    return `${value.toFixed(1)}%`;
};
// Default funnel colors
const funnelColors = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
export function Analytics() {
    const { t } = useTranslation();
    const { loading, error, refresh, metrics, exportCSV } = useAnalytics();
    const [dateRangePreset, setDateRangePreset] = useState('30days');
    const [exporting, setExporting] = useState(false);
    // Handle date range change
    const handleDateRangeChange = useCallback((preset) => {
        setDateRangePreset(preset);
        const to = new Date();
        const from = new Date();
        switch (preset) {
            case '7days':
                from.setDate(from.getDate() - 7);
                break;
            case '30days':
                from.setDate(from.getDate() - 30);
                break;
            case '90days':
                from.setDate(from.getDate() - 90);
                break;
            default:
                from.setDate(from.getDate() - 30);
        }
        const newRange = { from, to };
        refresh(newRange);
    }, [refresh]);
    // Handle export
    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await exportCSV();
        }
        catch (err) {
            console.error('Export failed:', err);
        }
        finally {
            setExporting(false);
        }
    }, [exportCSV]);
    // Handle refresh
    const handleRefresh = useCallback(() => {
        refresh();
    }, [refresh]);
    if (loading) {
        return (_jsx("div", { style: styles.loading, children: _jsxs("div", { style: styles.loadingContent, children: [_jsx(RefreshCw, { style: { ...styles.spinner, animation: 'spin 1s linear infinite' } }), _jsx("p", { style: { color: '#6b7280' }, children: t('loadingAnalytics') })] }) }));
    }
    if (error) {
        return (_jsx("div", { style: styles.page, children: _jsxs("div", { style: styles.errorCard, children: [_jsx("h3", { style: styles.errorTitle, children: t('errorLoadingAnalytics') }), _jsx("p", { style: styles.errorMessage, children: error.message }), _jsx("button", { onClick: handleRefresh, style: styles.retryButton, children: t('retry') })] }) }));
    }
    // Extract data from metrics
    const summary = metrics?.summary;
    const topQueries = metrics?.topQueries || [];
    const funnel = metrics?.funnel || [];
    const hourly = metrics?.hourly || [];
    const comparison = metrics?.comparison;
    // Get trend values from comparison data
    const getTrend = (field) => {
        if (!comparison?.changes)
            return { value: 0, direction: 'up' };
        const change = comparison.changes[field] || 0;
        return { value: Math.abs(change), direction: change >= 0 ? 'up' : 'down' };
    };
    const sessionsTrend = getTrend('sessions');
    const messagesTrend = getTrend('messages');
    const conversionsTrend = getTrend('conversions');
    return (_jsxs(motion.div, { style: styles.page, initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, ease: 'easeOut' }, children: [_jsxs("div", { style: styles.header, children: [_jsxs("div", { style: styles.headerContent, children: [_jsx("h1", { style: styles.title, children: t('analytics') }), _jsx("p", { style: styles.subtitle, children: t('analyticsDescription') }), metrics?.dateRange && (_jsxs("p", { style: styles.dateRangeInfo, children: [_jsx(Calendar, { style: { width: '12px', height: '12px', display: 'inline', marginRight: '4px' } }), metrics.dateRange.from, " to ", metrics.dateRange.to] }))] }), _jsxs("div", { style: styles.headerActions, children: [_jsxs("div", { style: styles.dateRangeSelector, children: [_jsx("button", { style: dateRangePreset === '7days' ? styles.dateRangeButtonActive : styles.dateRangeButton, onClick: () => handleDateRangeChange('7days'), children: t('days7') }), _jsx("button", { style: dateRangePreset === '30days' ? styles.dateRangeButtonActive : styles.dateRangeButton, onClick: () => handleDateRangeChange('30days'), children: t('days30') }), _jsx("button", { style: dateRangePreset === '90days' ? styles.dateRangeButtonActive : styles.dateRangeButton, onClick: () => handleDateRangeChange('90days'), children: t('days90') })] }), _jsx("button", { onClick: handleRefresh, style: styles.refreshButton, title: "Refresh data", children: _jsx(RefreshCw, { style: { width: '16px', height: '16px' } }) }), _jsxs("button", { onClick: handleExport, style: exporting ? styles.exportButtonDisabled : styles.exportButton, disabled: exporting, children: [_jsx(Download, { style: { width: '16px', height: '16px' } }), exporting ? t('exporting') : t('exportCsv')] })] })] }), _jsxs("div", { style: styles.statsGrid, children: [_jsxs(motion.div, { style: styles.statCard, whileHover: { y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: { duration: 0.2 }, children: [_jsx("div", { style: styles.iconBox, children: _jsx(MessageSquare, { style: { width: '20px', height: '20px', color: '#6366f1' } }) }), _jsxs("div", { style: styles.statContent, children: [_jsx("p", { style: styles.statLabel, children: t('totalSessions') }), _jsx("p", { style: styles.statValue, children: formatNumber(summary?.totalSessions) }), _jsxs("div", { style: styles.trendRow, children: [sessionsTrend.direction === 'up' ? (_jsx(ArrowUp, { style: { width: '16px', height: '16px', ...styles.trendUp } })) : (_jsx(ArrowDown, { style: { width: '16px', height: '16px', ...styles.trendDown } })), _jsxs("span", { style: { fontSize: '14px', ...(sessionsTrend.direction === 'up' ? styles.trendUp : styles.trendDown) }, children: [sessionsTrend.value.toFixed(1), "%"] }), _jsx("span", { style: styles.trendText, children: t('vsLastPeriod') })] })] })] }), _jsxs(motion.div, { style: styles.statCard, whileHover: { y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: { duration: 0.2 }, children: [_jsx("div", { style: styles.iconBox, children: _jsx(Users, { style: { width: '20px', height: '20px', color: '#6366f1' } }) }), _jsxs("div", { style: styles.statContent, children: [_jsx("p", { style: styles.statLabel, children: t('totalMessages') }), _jsx("p", { style: styles.statValue, children: formatNumber(summary?.totalMessages) }), _jsxs("div", { style: styles.trendRow, children: [messagesTrend.direction === 'up' ? (_jsx(ArrowUp, { style: { width: '16px', height: '16px', ...styles.trendUp } })) : (_jsx(ArrowDown, { style: { width: '16px', height: '16px', ...styles.trendDown } })), _jsxs("span", { style: { fontSize: '14px', ...(messagesTrend.direction === 'up' ? styles.trendUp : styles.trendDown) }, children: [messagesTrend.value.toFixed(1), "%"] }), _jsx("span", { style: styles.trendText, children: t('vsLastPeriod') })] })] })] }), _jsxs(motion.div, { style: styles.statCard, whileHover: { y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: { duration: 0.2 }, children: [_jsx("div", { style: styles.iconBox, children: _jsx(Clock, { style: { width: '20px', height: '20px', color: '#6366f1' } }) }), _jsxs("div", { style: styles.statContent, children: [_jsx("p", { style: styles.statLabel, children: t('avgSessionTime') }), _jsx("p", { style: styles.statValue, children: formatDuration(summary?.avgSessionDuration) }), _jsx("div", { style: styles.trendRow, children: _jsx("span", { style: styles.trendText, children: t('perConversation') }) })] })] }), _jsxs(motion.div, { style: styles.statCard, whileHover: { y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }, transition: { duration: 0.2 }, children: [_jsx("div", { style: styles.iconBox, children: _jsx(TrendingUp, { style: { width: '20px', height: '20px', color: '#6366f1' } }) }), _jsxs("div", { style: styles.statContent, children: [_jsx("p", { style: styles.statLabel, children: t('conversionRate') }), _jsx("p", { style: styles.statValue, children: formatPercentage(summary?.conversionRate) }), _jsxs("div", { style: styles.trendRow, children: [conversionsTrend.direction === 'up' ? (_jsx(ArrowUp, { style: { width: '16px', height: '16px', ...styles.trendUp } })) : (_jsx(ArrowDown, { style: { width: '16px', height: '16px', ...styles.trendDown } })), _jsxs("span", { style: { fontSize: '14px', ...(conversionsTrend.direction === 'up' ? styles.trendUp : styles.trendDown) }, children: [conversionsTrend.value.toFixed(1), "%"] }), _jsx("span", { style: styles.trendText, children: t('vsLastPeriod') })] })] })] })] }), _jsxs("div", { style: styles.chartsGrid, children: [_jsxs("div", { style: styles.chartCard, children: [_jsx("div", { style: styles.chartHeader, children: _jsx("h3", { style: styles.chartTitle, children: t('productEngagement') }) }), _jsx("div", { style: styles.chartPlaceholder, children: _jsxs("div", { style: styles.placeholderContent, children: [_jsx(ShoppingCart, { style: { width: '48px', height: '48px', margin: '0 auto 8px', opacity: 0.5 } }), _jsx("p", { style: { fontSize: '28px', fontWeight: 700, color: '#111827', margin: '0 0 8px 0' }, children: formatPercentage(summary?.productClickRate) }), _jsx("p", { style: { fontSize: '14px' }, children: t('productClickRate') })] }) }), _jsxs("div", { style: styles.chartFooter, children: [_jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: styles.footerValue, children: formatNumber(summary?.totalSessions) }), _jsx("p", { style: styles.footerLabel, children: t('sessions') })] }), _jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: styles.footerValue, children: formatNumber(summary?.totalMessages) }), _jsx("p", { style: styles.footerLabel, children: t('messages') })] }), _jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: { ...styles.footerValue, color: '#10b981' }, children: formatPercentage(summary?.completionRate) }), _jsx("p", { style: styles.footerLabel, children: "Completion Rate" })] })] })] }), _jsxs("div", { style: styles.chartCard, children: [_jsx("div", { style: styles.chartHeader, children: _jsx("h3", { style: styles.chartTitle, children: t('userEngagement') }) }), _jsx("div", { style: styles.chartPlaceholder, children: _jsxs("div", { style: styles.placeholderContent, children: [_jsx(BarChart3, { style: { width: '48px', height: '48px', margin: '0 auto 8px', opacity: 0.5 } }), _jsx("p", { style: { fontSize: '28px', fontWeight: 700, color: '#111827', margin: '0 0 8px 0' }, children: formatNumber(summary?.uniqueUsers || summary?.totalSessions) }), _jsx("p", { style: { fontSize: '14px' }, children: "Unique Users" })] }) }), _jsxs("div", { style: styles.chartFooter, children: [_jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: { ...styles.footerValue, color: '#10b981' }, children: formatPercentage(summary?.conversionRate) }), _jsx("p", { style: styles.footerLabel, children: "Conversion" })] }), _jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: { ...styles.footerValue, color: '#f59e0b' }, children: formatPercentage(summary?.productClickRate) }), _jsx("p", { style: styles.footerLabel, children: "Click Rate" })] }), _jsxs("div", { style: styles.footerStat, children: [_jsx("p", { style: { ...styles.footerValue, color: '#3b82f6' }, children: formatDuration(summary?.avgSessionDuration) }), _jsx("p", { style: styles.footerLabel, children: "Avg. Duration" })] })] })] })] }), _jsxs("div", { style: styles.chartsGrid, children: [_jsxs("div", { style: styles.queriesCard, children: [_jsx("div", { style: styles.chartHeader, children: _jsx("h3", { style: styles.chartTitle, children: t('topQueries') }) }), _jsx("div", { children: topQueries.length > 0 ? (topQueries.slice(0, 5).map((item, index) => (_jsxs("div", { style: index === Math.min(topQueries.length, 5) - 1 ? styles.queryRowLast : styles.queryRow, children: [_jsxs("div", { style: styles.queryLeft, children: [_jsx("span", { style: styles.queryRank, children: index + 1 }), _jsxs("div", { style: styles.queryInfo, children: [_jsx("p", { style: styles.queryText, children: item.query }), _jsxs("p", { style: styles.queryAsks, children: [item.count.toLocaleString(), " ", t('asks'), " (", formatPercentage(item.percentage), ")"] })] })] }), item.trend !== undefined && (_jsxs("div", { style: styles.queryTrend, children: [item.trendDirection === 'up' ? (_jsx(ArrowUp, { style: { width: '16px', height: '16px', color: '#10b981' } })) : (_jsx(ArrowDown, { style: { width: '16px', height: '16px', color: '#ef4444' } })), _jsxs("span", { style: { fontSize: '14px', color: item.trendDirection === 'up' ? '#10b981' : '#ef4444' }, children: [item.trend, "%"] })] }))] }, index)))) : (_jsx("div", { style: styles.noDataCard, children: _jsx("p", { children: "No query data available for this period" }) })) })] }), _jsxs("div", { style: styles.funnelCard, children: [_jsx("div", { style: styles.chartHeader, children: _jsx("h3", { style: styles.chartTitle, children: t('conversionFunnel') }) }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '16px' }, children: funnel.length > 0 ? (funnel.map((item, idx) => (_jsxs("div", { style: styles.funnelStage, children: [_jsxs("div", { style: styles.funnelHeader, children: [_jsx("span", { style: styles.funnelLabel, children: item.stage }), _jsxs("span", { style: styles.funnelValue, children: [item.count.toLocaleString(), " (", formatPercentage(item.percentage), ")"] })] }), _jsx("div", { style: styles.funnelBar, children: _jsx("div", { style: {
                                                    ...styles.funnelProgress,
                                                    width: `${Math.max(item.percentage, 1)}%`,
                                                    backgroundColor: item.color || funnelColors[idx % funnelColors.length],
                                                } }) })] }, idx)))) : (
                                // Default funnel stages when no data
                                [
                                    { stage: t('chatOpened'), count: 0, percentage: 100, color: '#6366f1' },
                                    { stage: t('messageSent'), count: 0, percentage: 0, color: '#3b82f6' },
                                    { stage: t('productViewed'), count: 0, percentage: 0, color: '#10b981' },
                                    { stage: t('addedToCart'), count: 0, percentage: 0, color: '#f59e0b' },
                                    { stage: t('purchased'), count: 0, percentage: 0, color: '#ef4444' },
                                ].map((item, idx) => (_jsxs("div", { style: styles.funnelStage, children: [_jsxs("div", { style: styles.funnelHeader, children: [_jsx("span", { style: styles.funnelLabel, children: item.stage }), _jsxs("span", { style: styles.funnelValue, children: [item.count.toLocaleString(), " (", item.percentage, "%)"] })] }), _jsx("div", { style: styles.funnelBar, children: _jsx("div", { style: {
                                                    ...styles.funnelProgress,
                                                    width: `${Math.max(item.percentage, 1)}%`,
                                                    backgroundColor: item.color,
                                                } }) })] }, idx)))) })] })] }), _jsxs("div", { style: styles.usageCard, children: [_jsx("div", { style: styles.usageHeader, children: _jsx("h3", { style: styles.usageTitle, children: t('usageByHour') }) }), _jsx(HourlyUsageChart, { data: hourly, height: 200 })] })] }));
}
